import React, { useEffect, useRef, useState } from "react";

export type ToastType = "success" | "error" | "warning" | "info" | "message" | "general";

interface Props {
    id: number;
    message: string;
    type: ToastType;
    duration?: number;
    onClose: () => void;
}

export default function ToastModal({ id, message, type, duration = 4000, onClose }: Props) {
    const config: Record<ToastType, { title: string; color: string; bg: string; border: string; progress: string; icon: React.ReactNode }> = {
        success: {
            title: "Success",
            color: "text-emerald-600",
            bg: "bg-emerald-50",
            border: "border-emerald-200",
            progress: "bg-emerald-500",
            icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4"><path d="m5 12 4 4L19 6" strokeLinecap="round" strokeLinejoin="round" /></svg>,
        },
        error: {
            title: "Error",
            color: "text-red-600",
            bg: "bg-red-50",
            border: "border-red-200",
            progress: "bg-red-500",
            icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4"><circle cx="12" cy="12" r="9" /><path d="m15 9-6 6m0-6 6 6" strokeLinecap="round" /></svg>,
        },
        warning: {
            title: "Warning",
            color: "text-amber-600",
            bg: "bg-amber-50",
            border: "border-amber-200",
            progress: "bg-amber-500",
            icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4"><path d="M10.3 3.9 2.6 17.2A2 2 0 0 0 4.3 20h15.4a2 2 0 0 0 1.7-2.8L13.7 3.9a2 2 0 0 0-3.4 0Z" strokeLinecap="round" strokeLinejoin="round" /><path d="M12 9v4m0 3h.01" strokeLinecap="round" /></svg>,
        },
        info: {
            title: "Information",
            color: "text-[#186cb2]",
            bg: "bg-[#186cb2]/5",
            border: "border-[#186cb2]/20",
            progress: "bg-[#186cb2]",
            icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4"><circle cx="12" cy="12" r="9" /><path d="M12 11v5m0-8h.01" strokeLinecap="round" /></svg>,
        },
        message: {
            title: "New Message",
            color: "text-[#186cb2]",
            bg: "bg-[#e3f1fa]",
            border: "border-[#c7e0f3]",
            progress: "bg-[#186cb2]",
            icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4"><path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8A8.5 8.5 0 0 1 8.7 3.9a8.4 8.4 0 0 1 3.8-.9h.5a8.5 8.5 0 0 1 8 8Z" strokeLinecap="round" strokeLinejoin="round" /></svg>,
        },
        general: {
            title: "Proposal Update",
            color: "text-violet-700",
            bg: "bg-violet-50",
            border: "border-violet-200",
            progress: "bg-violet-500",
            icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4"><path d="M12 8v4l3 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" strokeLinecap="round" strokeLinejoin="round" /></svg>,
        },
    };

    const current = config[type];
    const [isPaused, setIsPaused] = useState(false);
    const timerRef = useRef<number | null>(null);
    const startTimeRef = useRef<number>(Date.now());
    const remainingTimeRef = useRef<number>(duration);

    useEffect(() => {
        startTimeRef.current = Date.now();
        remainingTimeRef.current = duration;

        timerRef.current = window.setTimeout(onClose, remainingTimeRef.current);

        return () => {
            if (timerRef.current !== null) window.clearTimeout(timerRef.current);
        };
    }, [id, duration, onClose]);

    const handleMouseEnter = () => {
        setIsPaused(true);
        if (timerRef.current !== null) window.clearTimeout(timerRef.current);

        const elapsed = Date.now() - startTimeRef.current;
        remainingTimeRef.current = Math.max(0, remainingTimeRef.current - elapsed);
    };

    const handleMouseLeave = () => {
        setIsPaused(false);
        startTimeRef.current = Date.now();
        timerRef.current = window.setTimeout(onClose, remainingTimeRef.current);
    };

    return (
        <div className={`w-full overflow-hidden rounded-lg border bg-white shadow-md ${current.border}`} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <div className="flex items-center gap-3 px-3.5 py-3">
                <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${current.bg} ${current.color}`}>{current.icon}</div>
                <div className="min-w-0 flex-1">
                    <p className={`text-xs font-semibold leading-4 ${current.color}`}>{current.title}</p>
                    <p className="mt-0.5 text-[11px] leading-4 text-gray-500">{message}</p>
                </div>
                <button type="button" onClick={onClose} className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 active:scale-95" aria-label="Close notification">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-3.5 w-3.5"><path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" /></svg>
                </button>
            </div>
            <div className="h-0.5 w-full bg-gray-100">
                <div className={`h-full ${current.progress}`} style={{ animation: `toast-progress ${duration}ms linear forwards`, animationPlayState: isPaused ? "paused" : "running" }} />
            </div>
            <style>{`@keyframes toast-progress { from { width: 100%; } to { width: 0%; } }`}</style>
        </div>
    );
}